import { Modal, Form, Input, Typography } from 'antd'
import { SendOutlined } from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import { sendDeviceMessage } from '../../api/device.api'
import { toast, handleMutationError } from '../../lib/notification'

const { Text } = Typography

interface Props {
  deviceId: string
  open: boolean
  onClose: () => void
}

export default function NotifyDeviceModal({ deviceId, open, onClose }: Props) {
  const [form] = Form.useForm<{ message: string }>()

  const mutation = useMutation({
    mutationFn: (values: { message: string }) => sendDeviceMessage(deviceId, values),
    onSuccess: () => {
      toast.success('Message sent successfully')
      form.resetFields()
      onClose()
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to send message')
    },
  })

  const handleClose = () => {
    if (!mutation.isPending) {
      form.resetFields()
      onClose()
    }
  }

  return (
    <Modal
      title="Notify Device"
      open={open}
      onCancel={handleClose}
      onOk={() => form.submit()}
      okText="Send"
      okButtonProps={{ loading: mutation.isPending, icon: <SendOutlined /> }}
      cancelButtonProps={{ disabled: mutation.isPending }}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">Device:</Text>{' '}
        <Text strong style={{ fontFamily: 'monospace', color: '#6366f1' }}>
          {deviceId}
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        preserve={false}
        onFinish={(values) => mutation.mutate(values)}
      >
        <Form.Item
          name="message"
          label="Message"
          rules={[
            { required: true, message: 'Message is required' },
            { max: 2000, message: 'Message must not exceed 2000 characters' },
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder="Enter message to send to device..."
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
